/*
 * pack-builder.js
 *
 * Selector de sabores para los packs de precio fijo.
 *
 * Al carrito entra UNICAMENTE la variante del producto contenedor, cantidad 1.
 * Lo que elige el cliente viaja como line item properties, de forma que el
 * precio del pack no depende de los productos escogidos.
 *
 * No agrega los productos individuales al carrito y no descuenta su inventario:
 * es una limitacion asumida y acordada.
 *
 * Sin dependencias externas.
 */
(function () {
  'use strict';

  var MULT = '×'; // simbolo de multiplicacion

  /* ------------------------------------------------------------------ *
   * Acortado de nombres para el carrito
   *
   * Devuelven el trozo comun a TODOS los nombres del grupo, cortado en
   * frontera de palabra para no partir a media palabra. Si no hay nada en
   * comun devuelven cadena vacia y no se recorta nada.
   * ------------------------------------------------------------------ */
  function commonPrefix(names) {
    if (names.length < 2) return '';
    var p = names[0];
    for (var i = 1; i < names.length; i++) {
      var j = 0;
      while (j < p.length && j < names[i].length && p.charAt(j) === names[i].charAt(j)) j++;
      p = p.slice(0, j);
      if (!p) return '';
    }
    var k = p.lastIndexOf(' ');
    return k > 0 ? p.slice(0, k + 1) : '';
  }

  function commonSuffix(names) {
    if (names.length < 2) return '';
    var s = names[0];
    for (var i = 1; i < names.length; i++) {
      var a = s;
      var b = names[i];
      var j = 0;
      while (j < a.length && j < b.length &&
             a.charAt(a.length - 1 - j) === b.charAt(b.length - 1 - j)) j++;
      s = a.slice(a.length - j);
      if (!s) return '';
    }
    var k = s.indexOf(' ');
    return k >= 0 ? s.slice(k) : '';
  }

  function shorten(name, pre, suf) {
    var out = name;
    if (pre && out.indexOf(pre) === 0) out = out.slice(pre.length);
    if (suf && out.length > suf.length && out.slice(-suf.length) === suf) {
      out = out.slice(0, out.length - suf.length);
    }
    out = out.trim();
    // Si el recorte se lo come todo, mejor el nombre completo.
    return out.length ? out : name;
  }

  /* ------------------------------------------------------------------ *
   * Un grupo de seleccion (una coleccion + un cupo)
   * ------------------------------------------------------------------ */
  class PackBuilder extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.quota = parseInt(this.dataset.quota, 10) || 1;
      this.paid = parseInt(this.dataset.paid, 10) || this.quota;
      this.gift = parseInt(this.dataset.gift, 10) || 0;

      this.counterEl = this.querySelector('[data-pb-counter]');
      this.giftHintEl = this.querySelector('[data-pb-gift-hint]');
      this.limitsEl = this.querySelector('[data-pb-limits]');

      this.addEventListener('click', this.onClick.bind(this));
      this.render();
    }

    onClick(event) {
      var btn = event.target.closest('[data-pb-step]');
      if (!btn) return;

      var item = btn.closest('[data-pb-item]');
      var out = item.querySelector('[data-pb-qty]');
      var step = parseInt(btn.dataset.pbStep, 10);
      var current = parseInt(out.textContent, 10) || 0;
      var next = current + step;

      if (next < 0) return;
      // No se puede pasar del cupo del grupo.
      if (step > 0 && this.total() >= this.quota) return;
      // Ni del tope propio del sabor, si lo tiene.
      var itemMax = parseInt(item.dataset.pbMax, 10);
      if (step > 0 && itemMax > 0 && current >= itemMax) return;

      out.textContent = next;
      item.classList.toggle('is-selected', next > 0);

      this.render();
      sync();
    }

    /* Suma de unidades elegidas en este grupo. */
    total() {
      var n = 0;
      this.querySelectorAll('[data-pb-qty]').forEach(function (o) {
        n += parseInt(o.textContent, 10) || 0;
      });
      return n;
    }

    get complete() {
      return this.dataset.unavailable !== 'true' && this.total() === this.quota;
    }

    get missing() {
      return this.quota - this.total();
    }

    /* Texto agregado para la line item property: "4 × Chocochip, 6 × Naranja" */
    get summary() {
      var all = [];
      this.querySelectorAll('[data-pb-item]').forEach(function (item) {
        all.push(item.dataset.pbName);
      });

      // Los nombres de un grupo comparten prefijo y sufijo ("Galletas
      // Chocochip - 40g", "Galletas Naranja - 40g"). Repetirlos en cada entrada
      // desborda la linea del carrito, y ademas ya vienen dichos en la etiqueta
      // del grupo. Se recortan para dejar solo el sabor.
      var pre = commonPrefix(all);
      var suf = commonSuffix(all);

      var parts = [];
      this.querySelectorAll('[data-pb-item]').forEach(function (item) {
        var out = item.querySelector('[data-pb-qty]');
        var qty = out ? parseInt(out.textContent, 10) || 0 : 0;
        if (qty < 1) return;

        var name = shorten(item.dataset.pbName, pre, suf);
        // Con una sola unidad el "1 x" sobra y ensucia la linea del carrito.
        // Solo se antepone la cantidad cuando hay repeticion de verdad.
        parts.push(qty === 1 ? name : qty + ' ' + MULT + ' ' + name);
      });
      return parts.join(', ');
    }

    render() {
      var total = this.total();

      if (this.counterEl) this.counterEl.textContent = total + ' / ' + this.quota;
      this.classList.toggle('is-complete', this.complete);

      // Se apaga el "+" por dos motivos distintos: porque el grupo ya llego a
      // su cupo, o porque ese sabor concreto llego a su propio tope.
      var atLimit = total >= this.quota;
      this.querySelectorAll('[data-pb-item]').forEach(function (item) {
        var out = item.querySelector('[data-pb-qty]');
        if (!out) return;
        var qty = parseInt(out.textContent, 10) || 0;
        var itemMax = parseInt(item.dataset.pbMax, 10);
        var ownLimit = itemMax > 0 && qty >= itemMax;

        var plus = item.querySelector('[data-pb-step="1"]');
        if (plus) plus.disabled = atLimit || ownLimit;

        var minus = item.querySelector('[data-pb-step="-1"]');
        if (minus) minus.disabled = qty === 0;

        item.classList.toggle('is-capped', ownLimit);
      });

      this.renderLimits();

      // Revelado del regalo: aparece al cruzar las unidades pagadas.
      if (this.giftHintEl && this.gift > 0) {
        this.giftHintEl.hidden = total < this.paid;
      }
    }

    /*
     * Aviso de los sabores con tope propio. Se arma desde el DOM en vez de
     * escribirlo a mano en el tema, para que no pueda quedarse desfasado si la
     * clienta cambia un tope o mete un sabor nuevo con el suyo.
     */
    renderLimits() {
      if (!this.limitsEl) return;

      var all = [];
      this.querySelectorAll('[data-pb-item]').forEach(function (item) {
        all.push(item.dataset.pbName);
      });
      var pre = commonPrefix(all);
      var suf = commonSuffix(all);

      var avisos = [];
      this.querySelectorAll('[data-pb-item][data-pb-max]').forEach(function (item) {
        var mx = parseInt(item.dataset.pbMax, 10);
        if (!(mx > 0)) return;
        var name = shorten(item.dataset.pbName, pre, suf);
        avisos.push('máximo ' + mx + (mx === 1 ? ' unidad de ' : ' unidades de ') + name);
      });

      if (!avisos.length) {
        this.limitsEl.hidden = true;
        this.limitsEl.textContent = '';
        return;
      }
      this.limitsEl.hidden = false;
      this.limitsEl.textContent = avisos.join(' · ');
    }
  }

  if (!customElements.get('pack-builder')) {
    customElements.define('pack-builder', PackBuilder);
  }

  /* ------------------------------------------------------------------ *
   * Coordinacion a nivel de pagina
   *
   * Un pack puede tener varios grupos (ej. 1 galleta + 1 granola + 1 brownie).
   * El boton de agregar al carrito solo se habilita cuando TODOS estan completos.
   *
   * Se trabaja sobre TODOS los formularios y botones de compra de la pagina, no
   * sobre uno concreto, porque el tema pinta un segundo boton en la barra fija
   * inferior. Ese boton vive FUERA del formulario y se engancha con el atributo
   * form=, asi que un querySelector dentro del formulario no lo encuentra: si no
   * se cubre, el cliente puede saltarse el cupo comprando desde la barra fija.
   * Ademas, en uno de sus dos estilos la barra crea su propio formulario, al que
   * tambien hay que copiar las propiedades.
   *
   * Ojo: NO vale barrer toda la pagina. La ficha trae mas formularios de
   * /cart/add (el bloque "Combinalo con" y sus botones de compra rapida).
   * Bloquearlos romperia esos productos, y escribirles las propiedades del pack
   * les colgaria elecciones que no les corresponden. Por eso se filtra por la
   * variante: solo cuentan los formularios que compran ESTE producto.
   * ------------------------------------------------------------------ */
  function sync() {
    var formIds = new Set();
    document.querySelectorAll('pack-builder').forEach(function (b) { formIds.add(b.dataset.formId); });
    formIds.forEach(syncForm);
  }

  function syncForm(formId) {
    var builders = Array.from(document.querySelectorAll('pack-builder')).filter(function (b) {
      return b.dataset.formId === formId;
    });
    if (!builders.length) return;

    var mainForm = document.getElementById(builders[0].dataset.formId);
    if (!mainForm) return;

    var idInput = mainForm.querySelector('[name="id"]');
    var variantId = idInput ? idInput.value : null;

    var forms = [];
    document.querySelectorAll('form[action*="/cart/add"]').forEach(function (f) {
      if (f === mainForm) {
        forms.push(f);
        return;
      }
      var el = f.querySelector('[name="id"]');
      if (variantId && el && el.value === variantId) forms.push(f);
    });

    // Botones dentro de esos formularios, mas los que viven fuera y se enganchan
    // con el atributo form= (el caso de la barra fija en estilo minimalista).
    var buttons = [];
    forms.forEach(function (f) {
      f.querySelectorAll('[name="add"]').forEach(function (b) {
        buttons.push(b);
      });
      if (f.id) {
        document
          .querySelectorAll('[name="add"][form="' + f.id + '"]')
          .forEach(function (b) {
            if (buttons.indexOf(b) === -1) buttons.push(b);
          });
      }
    });

    var pending = [];
    builders.forEach(function (b) {
      if (!b.complete) pending.push({ label: b.dataset.label, missing: b.missing });
    });

    forms.forEach(function (f) {
      writeProperties(f, builders);
      if (!f._packSubmitGuard) {
        f._packSubmitGuard = true;
        f.addEventListener('submit', function (event) {
          sync();
          var current = Array.from(document.querySelectorAll('pack-builder')).filter(function (b) {
            return b.dataset.formId === formId;
          });
          if (!current.length || current.some(function (b) { return !b.complete; }) ||
              (mainForm.querySelector('[data-pb-variant]') || {}).dataset?.pbAvailable === 'false') {
            event.preventDefault();
            event.stopImmediatePropagation();
          }
        }, true);
      }
    });
    updateSubmit(buttons, pending, mainForm);
  }

  /* Mantiene los inputs ocultos de properties dentro del formulario. */
  function writeProperties(form, builders) {
    form.querySelectorAll('[data-pb-property]').forEach(function (el) {
      el.remove();
    });

    var used = {};
    builders.forEach(function (b) {
      if (!b.complete) return;

      // Si dos grupos comparten etiqueta, se numeran para no pisarse.
      var label = b.dataset.label;
      used[label] = (used[label] || 0) + 1;
      if (used[label] > 1) label = label + ' ' + used[label];

      var input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[' + label + ']';
      input.value = b.summary;
      input.setAttribute('data-pb-property', '');
      form.appendChild(input);
    });
  }

  /* Habilita o bloquea TODOS los botones de compra y explica que falta. */
  function updateSubmit(buttons, pending, mainForm) {
    if (!buttons.length) return;

    var variant = mainForm.querySelector('[data-pb-variant]');
    var incomplete = pending.length > 0 || (variant && variant.dataset.pbAvailable === 'false');
    buttons.forEach(function (b) {
      b.disabled = incomplete;
    });

    // El aviso se pinta una sola vez, junto al boton principal del formulario.
    var anchor = mainForm ? mainForm.querySelector('[name="add"]') : buttons[0];
    if (!anchor) return;

    var status = document.querySelector('[data-pb-status]');
    if (!status) {
      status = document.createElement('p');
      status.className = 'pack-builder__status';
      status.setAttribute('data-pb-status', '');
      status.setAttribute('aria-live', 'polite');
      var holder = anchor.closest('.product-form__buttons') || anchor.parentNode;
      holder.parentNode.insertBefore(status, holder);
    }

    if (!pending.length) {
      status.textContent = '';
      status.hidden = true;
      return;
    }

    status.hidden = false;
    status.textContent =
      'Te falta elegir: ' +
      pending
        .map(function (p) {
          return p.missing + ' de ' + p.label;
        })
        .join(' y ');
  }

  /* ------------------------------------------------------------------ *
   * Arranque
   * ------------------------------------------------------------------ */
  function init() {
    sync();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // El editor de temas vuelve a montar la seccion al editarla.
  document.addEventListener('shopify:section:load', init);

  // Nota: no se usa un MutationObserver global. sync() escribe inputs dentro del
  // formulario, asi que observar el DOM y reaccionar volviendo a sincronizar se
  // realimenta en bucle. Los packs del Grupo B tienen una sola variante, de modo
  // que el tema no repinta el bloque de compra por cambio de variante y no hace
  // falta vigilarlo.
})();
